import Repository from '@/repositories/repository';
import {
	ModuleRole,
	SystemClaim,
	satisfiesModuleRole,
	toClaimArray,
} from '@/common/rbac';
import { UI_PREFERENCES_SYNC_DEBOUNCE_MS } from '../settings.constants';
import type {
	ColorMode,
	UIPreferencesPayload,
	UserPreferenceBasePayload,
} from '../settings.model';
import {
	applyColorMode,
	hasOwn,
	isColorMode,
	normalizeAdvancedFilterCollapse,
	normalizeAdvancedFilterLayouts,
	normalizeDataListPreferences,
	normalizeFilesTablePreferences,
	normalizeSplitPanelWidths,
	normalizeSplitRouteMemory,
	persistAdvancedFilterCollapse,
	persistAdvancedFilterLayouts,
	persistColorMode,
	persistCustomFieldsManagerShowDefaultFields,
	persistDataListPreferences,
	persistFilesTablePreferences,
	persistSplitPanelWidths,
	persistSplitRouteMemory,
	persistSplitViewEnabled,
	resolveInitialColorMode,
	resolveInitialCustomFieldsManagerShowDefaultFields,
	resolveInitialSplitViewEnabled,
	resolveUserPreferenceBasePayload,
} from '../settings.utils';

let uiPreferencesSyncTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Global UI preference actions.
 *
 * Manages startup initialization, applying server preferences and debounced
 * upsert to `/userpreference/upsert`.
 */
// Claims come from the session rather than the auth store: importing the store
// here would close a cycle (stores/settings -> this module -> stores/auth ->
// @/repositories -> repositories/userPreferences -> stores/settings). The logic
// mirrors authStore.can() — Root bypasses, platform:owner satisfies
// platform:user additively.
const canSyncUserPreferences = (): boolean => {
	const claims = toClaimArray(sessionStorage.getItem('user_claims'));
	return (
		claims.includes(SystemClaim.Root) ||
		satisfiesModuleRole(claims, ModuleRole.PlatformUser)
	);
};

export const uiPreferencesActions = {
	/**
	 * Applies local startup defaults (color mode and split view state).
	 */
	initialize(this: any) {
		const initialMode = resolveInitialColorMode();
		this.colorMode = initialMode;
		applyColorMode(initialMode);
		this.splitViewEnabled = resolveInitialSplitViewEnabled();
		this.customFieldsManagerShowDefaultFields =
			resolveInitialCustomFieldsManagerShowDefaultFields();
	},
	/**
	 * Creates the serializable UI preference payload for backend persistence.
	 */
	buildUIPreferencesPayload(this: any): UIPreferencesPayload {
		return {
			colorMode: this.colorMode,
			splitViewEnabled: this.splitViewEnabled,
			customFieldsManagerShowDefaultFields:
				this.customFieldsManagerShowDefaultFields,
			dataListPreferences: JSON.parse(
				JSON.stringify(this.dataListPreferences)
			) as Record<string, unknown>,
			filesTablePreferences: JSON.parse(
				JSON.stringify(this.filesTablePreferences)
			) as Record<string, unknown>,
			splitPanelWidths: { ...this.splitPanelWidths },
			advancedFilterLayouts: { ...this.advancedFilterLayouts },
			advancedFilterCollapse: { ...this.advancedFilterCollapse },
			splitRouteMemory: JSON.parse(
				JSON.stringify(this.splitRouteMemory)
			) as Record<string, unknown>,
		};
	},
	/**
	 * Sends a preference upsert request to the backend.
	 */
	async upsertUserPreferences(this: any, payload: UserPreferenceBasePayload) {
		const upsertPayload = {
			...(payload || {}),
			uiPreferences: JSON.stringify(this.buildUIPreferencesPayload()),
		};

		return Repository.postWithAuth('/userpreference/upsert', upsertPayload);
	},
	/**
	 * Debounces preference sync calls to avoid excessive network requests.
	 */
	queueUIPreferencesUpsert(this: any): void {
		if (this.isApplyingUIPreferences) {
			return;
		}

		// POST /userpreference/upsert is AllowRoles(platform:user) (thanos-api
		// user_preference.go:30). This fires from ~24 places including the navbar
		// dark-mode toggle, and the failure is swallowed below, so without this a
		// module-only user silently 403s on every preference change and each one
		// is reported to Sentry. Their preferences stay local instead.
		if (!canSyncUserPreferences()) {
			return;
		}

		if (!resolveUserPreferenceBasePayload()) {
			return;
		}

		if (uiPreferencesSyncTimeout) {
			clearTimeout(uiPreferencesSyncTimeout);
		}

		uiPreferencesSyncTimeout = setTimeout(() => {
			uiPreferencesSyncTimeout = null;
			const payload = resolveUserPreferenceBasePayload();
			if (!payload) {
				return;
			}

			void this.upsertUserPreferences(payload).catch(() => {
				// Silent fail: local preferences remain persisted and retry on next change.
			});
		}, UI_PREFERENCES_SYNC_DEBOUNCE_MS);
	},
	/**
	 * Applies UI preference payload received from backend and updates local cache.
	 */
	applyUIPreferences(this: any, preferences: unknown): void {
		if (
			!preferences ||
			typeof preferences !== 'object' ||
			Array.isArray(preferences)
		) {
			return;
		}

		this.isApplyingUIPreferences = true;
		try {
			if (isColorMode((preferences as Record<string, unknown>).colorMode)) {
				this.setColorMode(
					(preferences as Record<string, unknown>).colorMode as ColorMode
				);
			}

			if (
				typeof (preferences as Record<string, unknown>).splitViewEnabled ===
				'boolean'
			) {
				this.setSplitViewEnabled(
					(preferences as Record<string, unknown>).splitViewEnabled as boolean
				);
			}

			if (
				typeof (preferences as Record<string, unknown>)
					.customFieldsManagerShowDefaultFields === 'boolean'
			) {
				this.setCustomFieldsManagerShowDefaultFields(
					(preferences as Record<string, unknown>)
						.customFieldsManagerShowDefaultFields as boolean
				);
			}

			if (
				hasOwn(preferences as Record<string, unknown>, 'dataListPreferences')
			) {
				this.dataListPreferences = normalizeDataListPreferences(
					(preferences as Record<string, unknown>).dataListPreferences
				);
				persistDataListPreferences(this.dataListPreferences);
			}

			if (
				hasOwn(preferences as Record<string, unknown>, 'filesTablePreferences')
			) {
				this.filesTablePreferences = normalizeFilesTablePreferences(
					(preferences as Record<string, unknown>).filesTablePreferences
				);
				persistFilesTablePreferences(this.filesTablePreferences);
			}

			if (hasOwn(preferences as Record<string, unknown>, 'splitPanelWidths')) {
				this.splitPanelWidths = normalizeSplitPanelWidths(
					(preferences as Record<string, unknown>).splitPanelWidths
				);
				persistSplitPanelWidths(this.splitPanelWidths);
			}

			if (
				hasOwn(preferences as Record<string, unknown>, 'advancedFilterLayouts')
			) {
				const serverLayouts = normalizeAdvancedFilterLayouts(
					(preferences as Record<string, unknown>).advancedFilterLayouts
				);
				// Keep local store as source of truth when server payload is stale/empty.
				// Server-only keys are still merged in.
				this.advancedFilterLayouts = {
					...serverLayouts,
					...this.advancedFilterLayouts,
				};
				persistAdvancedFilterLayouts(this.advancedFilterLayouts);
			}

			if (
				hasOwn(preferences as Record<string, unknown>, 'advancedFilterCollapse')
			) {
				const serverCollapse = normalizeAdvancedFilterCollapse(
					(preferences as Record<string, unknown>).advancedFilterCollapse
				);
				// Keep local store as source of truth when server payload is stale/empty.
				// Server-only keys are still merged in.
				this.advancedFilterCollapse = {
					...serverCollapse,
					...this.advancedFilterCollapse,
				};
				persistAdvancedFilterCollapse(this.advancedFilterCollapse);
			}

			if (hasOwn(preferences as Record<string, unknown>, 'splitRouteMemory')) {
				this.splitRouteMemory = normalizeSplitRouteMemory(
					(preferences as Record<string, unknown>).splitRouteMemory
				);
				persistSplitRouteMemory(this.splitRouteMemory);
			}
		} finally {
			this.isApplyingUIPreferences = false;
		}
	},
	/**
	 * Updates the active color mode and persists/syncs it.
	 */
	setColorMode(this: any, mode: ColorMode) {
		this.colorMode = mode;
		applyColorMode(mode);
		persistColorMode(mode);
		this.queueUIPreferencesUpsert();
	},
	/**
	 * Toggles color mode between dark and light.
	 */
	toggleColorMode(this: any) {
		this.setColorMode(this.colorMode === 'dark' ? 'light' : 'dark');
	},
	/**
	 * Updates split-view enablement and persists/syncs it.
	 */
	setSplitViewEnabled(this: any, isEnabled: boolean) {
		this.splitViewEnabled = Boolean(isEnabled);
		persistSplitViewEnabled(this.splitViewEnabled);
		this.queueUIPreferencesUpsert();
	},
	setCustomFieldsManagerShowDefaultFields(this: any, isVisible: boolean) {
		this.customFieldsManagerShowDefaultFields = Boolean(isVisible);
		persistCustomFieldsManagerShowDefaultFields(
			this.customFieldsManagerShowDefaultFields
		);
		this.queueUIPreferencesUpsert();
	},
	toggleCustomFieldsManagerShowDefaultFields(this: any) {
		this.setCustomFieldsManagerShowDefaultFields(
			!this.customFieldsManagerShowDefaultFields
		);
	},
	/**
	 * Toggles split-view enablement.
	 */
	toggleSplitViewEnabled(this: any) {
		this.setSplitViewEnabled(!this.splitViewEnabled);
	},
	/**
	 * Resets persisted UI preferences to defaults and clears table/layout state.
	 */
	resetUIPreferencesToDefaults(
		this: any,
		options: { queueUpsert?: boolean } = {}
	): void {
		this.colorMode = 'light';
		applyColorMode(this.colorMode);
		persistColorMode(this.colorMode);

		this.splitViewEnabled = false;
		persistSplitViewEnabled(this.splitViewEnabled);

		this.customFieldsManagerShowDefaultFields = true;
		persistCustomFieldsManagerShowDefaultFields(
			this.customFieldsManagerShowDefaultFields
		);

		this.dataListPreferences = {};
		persistDataListPreferences(this.dataListPreferences);

		this.filesTablePreferences = {};
		persistFilesTablePreferences(this.filesTablePreferences);

		this.splitPanelWidths = {};
		persistSplitPanelWidths(this.splitPanelWidths);

		this.advancedFilterLayouts = {};
		persistAdvancedFilterLayouts(this.advancedFilterLayouts);

		this.advancedFilterCollapse = {};
		persistAdvancedFilterCollapse(this.advancedFilterCollapse);

		this.splitRouteMemory = {};
		persistSplitRouteMemory(this.splitRouteMemory);

		// Selection memory is ephemeral and should also be reset.
		this.dataListSelectionMemory = {};
		this.dataListSelectionPanelMemory = {};

		if (options.queueUpsert !== false) {
			this.queueUIPreferencesUpsert();
		}
	},
};
